import React from 'react';
import classes from './Sidebar.module.css';
import SidebarItem from './SidebarItem/SidebarItem';
import extendCollapseIcon from '../../assets/icons/back-arrow.svg';
import extendCollapseIcon2 from '../../assets/icons/back-arrow2.svg';
import logo from '../../assets/images/new-logo.svg';
import { useAppSelector } from '../../store/store';
import { IAccountInfo, selectAccountInfo, selectUserRole } from '../../store/slices/auth';
import { selectNotifications, NotificationObj } from '../../store/slices/notifications';
import { useTranslation } from '../../hooks/useTranslations';
import { ROLE_SIDEBAR_CONFIG, SidebarEl } from './SidebarConfig';

interface SidebarProps {
  isExtended: boolean;
  setIsExtended: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isExtended, setIsExtended }) => {
  const { translations } = useTranslation();
  const userRole: string = useAppSelector<string>(selectUserRole);
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const notifications = useAppSelector(selectNotifications);

  const chattingNotifications = notifications.filter(
    (el: NotificationObj) => el.type === 'chatting_message_received'
  );
  const unReadedConversations = chattingNotifications.filter(
    (el: NotificationObj, index: number, self: any) =>
      index === self.findIndex((t: any) => t.conversation_id === el.conversation_id)
  ).length;

  /**
   * Build sidebar items based on user role and account capabilities
   * Filters out chat items if chat is not supported
   */
  const buildSidebarItems = (role: string, acctInfo: IAccountInfo): SidebarEl[] => {
    const roleItems = ROLE_SIDEBAR_CONFIG[role] || [];

    return roleItems.filter((item) => {
      // Filter out chat items if chat is not supported
      if (item.label === 'sidebar.chat' && !acctInfo.supports_chat) {
        return false;
      }
      return true;
    });
  };

  /**
   * Determine if a sidebar item should show notification indicator
   */
  const getNotificationIndicator = (item: SidebarEl): number | undefined => {
    if (item.navigateTo === '/web/chat') {
      return unReadedConversations;
    }
    return undefined;
  };

  const sidebarItems = buildSidebarItems(userRole, accountInfo);

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <div className={classes.sidebarIcon}>
          <img
            src={isExtended ? extendCollapseIcon2 : extendCollapseIcon}
            alt="extend"
            onClick={() => setIsExtended()}
            className={`${isExtended ? classes.collapseIconAbsolute : classes.extendIconAbsolute}`}
          />
        </div>
        <div className={`${classes.sidebarOptions} ${!isExtended ? classes.collapsed : ''}`}>
          {isExtended && <img className={classes.logo} src={logo} alt="ecocare" />}
          {sidebarItems.map((item) => (
            <SidebarItem
              key={item.label}
              label={translations(item.label)}
              isCollapsed={!isExtended}
              icon={item.icon}
              subItems={item.subItems?.map((subItem) => ({
                label: translations(subItem.label),
                navigateTo: subItem.navigateTo,
              }))}
              setIsExtended={() => setIsExtended()}
              navigateTo={item.navigateTo}
              notificationIndicator={getNotificationIndicator(item)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
